import React, { useEffect, useState, useCallback } from 'react';
import { FileText, Sparkles, Loader2, Trash2, Plus, ExternalLink, Save, Globe, Eye, Copy, Check } from 'lucide-react';
import { suiteAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Panel, PanelLoader, EmptyState, Card, Field, Chip, inputCls, copyText } from './shared';
import { toast } from 'sonner';

const uid = () => Math.random().toString(36).slice(2, 10);
const SECTION_TYPES = [['hero', 'Hero'], ['benefits', 'Benefits'], ['features', 'Features'], ['testimonials', 'Testimonials'], ['form', 'Lead form'], ['faq', 'FAQ'], ['cta', 'Closing CTA']];

export default function PagesPanel({ campaigns = [] }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [brief, setBrief] = useState('');
  const [busy, setBusy] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    suiteAPI.listPages().then(r => setPages(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const aiGenerate = async () => {
    if (!brief.trim()) return toast.error('Describe the offer');
    setBusy('ai');
    try {
      const { data } = await suiteAPI.aiPage({ brief });
      setEditing(data); setBrief(''); load();
      toast.success('Landing page drafted');
    } catch { toast.error('Generation failed'); }
    finally { setBusy(''); }
  };

  const save = async () => {
    setBusy('save');
    try {
      const { data } = await suiteAPI.savePage(editing);
      setEditing(data); load(); toast.success('Saved');
    } catch { toast.error('Save failed'); }
    finally { setBusy(''); }
  };

  const publish = async () => {
    const { data } = await suiteAPI.savePage({ ...editing, published: !editing.published });
    setEditing(data); load();
    toast.success(data.published ? 'Page is live' : 'Page unpublished');
  };

  const patchSection = (id, changes) => setEditing(p => ({ ...p, sections: p.sections.map(s => s.id === id ? { ...s, ...changes } : s) }));
  const publicUrl = editing ? `${window.location.origin}/page/${editing.slug}` : '';

  if (editing) {
    return (
      <Panel title="Landing Page Builder" subtitle={editing.published ? `Live at /page/${editing.slug}` : 'Draft — publish when you are ready'} testId="page-builder"
        action={
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setEditing(null)} className="bg-zinc-800 text-white">Back</Button>
            <Button onClick={save} disabled={busy === 'save'} className="bg-zinc-800 text-white" data-testid="page-save">
              {busy === 'save' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Save size={14} className="mr-1.5" />} Save
            </Button>
            <Button onClick={publish} className={editing.published ? 'bg-zinc-800 text-white' : 'bg-[#b8ff00] text-black font-bold'} data-testid="page-publish">
              <Globe size={14} className="mr-1.5" /> {editing.published ? 'Unpublish' : 'Publish'}
            </Button>
            {editing.published && (
              <a href={publicUrl} target="_blank" rel="noreferrer"><Button className="bg-zinc-800 text-white" data-testid="page-view"><ExternalLink size={14} /></Button></a>
            )}
          </div>
        }>
        <div className="grid xl:grid-cols-[380px_1fr] gap-5">
          <div className="space-y-3">
            <Card className="p-5 space-y-3">
              <Field label="Page name"><Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} className={inputCls} data-testid="page-name" /></Field>
              <Field label="URL slug"><Input value={editing.slug} onChange={e => setEditing({ ...editing, slug: e.target.value })} className={inputCls} data-testid="page-slug" /></Field>
              {editing.id && (
                <div className="flex items-center gap-2">
                  <Input value={publicUrl} readOnly className={`${inputCls} text-[11px]`} />
                  <Button size="sm" variant="ghost" className="text-zinc-400" onClick={() => { copyText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>{copied ? <Check size={13} /> : <Copy size={13} />}</Button>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <Field label="Background"><input type="color" value={editing.theme?.background || '#0a0a0a'} onChange={e => setEditing({ ...editing, theme: { ...editing.theme, background: e.target.value } })} className="w-full h-8 bg-zinc-800 rounded" /></Field>
                <Field label="Text"><input type="color" value={editing.theme?.text || '#ffffff'} onChange={e => setEditing({ ...editing, theme: { ...editing.theme, text: e.target.value } })} className="w-full h-8 bg-zinc-800 rounded" /></Field>
                <Field label="Accent"><input type="color" value={editing.theme?.accent || '#b8ff00'} onChange={e => setEditing({ ...editing, theme: { ...editing.theme, accent: e.target.value } })} className="w-full h-8 bg-zinc-800 rounded" /></Field>
              </div>
              <Field label="Campaign">
                <select value={editing.campaign_id || ''} onChange={e => setEditing({ ...editing, campaign_id: e.target.value })} className="w-full bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm px-3 py-2">
                  <option value="">None</option>
                  {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="SEO title"><Input value={editing.seo?.title || ''} onChange={e => setEditing({ ...editing, seo: { ...editing.seo, title: e.target.value } })} className={inputCls} /></Field>
              <Field label="SEO description"><Textarea rows={2} value={editing.seo?.description || ''} onChange={e => setEditing({ ...editing, seo: { ...editing.seo, description: e.target.value } })} className={inputCls} /></Field>
            </Card>
            <Card className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Add section</p>
              <div className="flex flex-wrap gap-1.5">
                {SECTION_TYPES.map(([v, l]) => (
                  <Chip key={v} testId={`page-add-${v}`} onClick={() => setEditing(p => ({
                    ...p, sections: [...(p.sections || []), { id: uid(), type: v, title: l, headline: '', subheadline: '', items: [], cta_text: 'Get started', button_text: 'Submit' }],
                  }))}>{l}</Chip>
                ))}
              </div>
            </Card>
            <Card className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Lead form fields</p>
              {(editing.form_fields || []).map((f, i) => (
                <div key={i} className="flex items-center gap-2 py-1">
                  <Input value={f.label} onChange={e => setEditing(p => ({ ...p, form_fields: p.form_fields.map((x, j) => j === i ? { ...x, label: e.target.value, name: e.target.value.toLowerCase().replace(/\s+/g, '_') } : x) }))} className={`${inputCls} h-7 text-xs`} />
                  <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => setEditing(p => ({ ...p, form_fields: p.form_fields.filter((_, j) => j !== i) }))}><Trash2 size={12} /></Button>
                </div>
              ))}
              <Button size="sm" className="w-full mt-2 bg-zinc-800 text-white text-xs" onClick={() => setEditing(p => ({ ...p, form_fields: [...(p.form_fields || []), { name: 'field', label: 'New field', type: 'text', required: false }] }))}><Plus size={12} className="mr-1" /> Add field</Button>
            </Card>
          </div>

          <div className="space-y-3">
            {(editing.sections || []).map(s => (
              <Card key={s.id} className="p-4" data-testid={`page-section-${s.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] uppercase tracking-wider text-[#b8ff00]">{s.type}</span>
                  <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => setEditing(p => ({ ...p, sections: p.sections.filter(x => x.id !== s.id) }))}><Trash2 size={12} /></Button>
                </div>
                {['hero', 'cta'].includes(s.type) && (
                  <div className="space-y-2">
                    <Input value={s.headline || ''} onChange={e => patchSection(s.id, { headline: e.target.value })} placeholder="Headline" className={inputCls} />
                    <Textarea rows={2} value={s.subheadline || ''} onChange={e => patchSection(s.id, { subheadline: e.target.value })} placeholder="Subheadline" className={inputCls} />
                    <Input value={s.cta_text || s.button_text || ''} onChange={e => patchSection(s.id, { cta_text: e.target.value, button_text: e.target.value })} placeholder="Button text" className={inputCls} />
                    {s.type === 'hero' && <Input value={s.image_url || ''} onChange={e => patchSection(s.id, { image_url: e.target.value })} placeholder="Hero image URL (optional)" className={inputCls} />}
                  </div>
                )}
                {['benefits', 'features', 'testimonials', 'faq'].includes(s.type) && (
                  <div className="space-y-2">
                    <Input value={s.title || ''} onChange={e => patchSection(s.id, { title: e.target.value })} placeholder="Section title" className={inputCls} />
                    {(s.items || []).map((it, i) => (
                      <div key={i} className="grid md:grid-cols-2 gap-2 items-start">
                        <Input value={it.title || it.quote || ''} onChange={e => patchSection(s.id, { items: s.items.map((x, j) => j === i ? { ...x, [it.quote !== undefined ? 'quote' : 'title']: e.target.value } : x) })} placeholder="Title / quote" className={`${inputCls} text-xs`} />
                        <div className="flex gap-1">
                          <Input value={it.description || it.author || ''} onChange={e => patchSection(s.id, { items: s.items.map((x, j) => j === i ? { ...x, [it.author !== undefined ? 'author' : 'description']: e.target.value } : x) })} placeholder="Description / author" className={`${inputCls} text-xs`} />
                          <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => patchSection(s.id, { items: s.items.filter((_, j) => j !== i) })}><Trash2 size={12} /></Button>
                        </div>
                      </div>
                    ))}
                    <Button size="sm" className="bg-zinc-800 text-white text-xs" onClick={() => patchSection(s.id, { items: [...(s.items || []), { title: '', description: '' }] })}><Plus size={12} className="mr-1" /> Add item</Button>
                  </div>
                )}
                {s.type === 'form' && (
                  <div className="space-y-2">
                    <Input value={s.title || ''} onChange={e => patchSection(s.id, { title: e.target.value })} placeholder="Form heading" className={inputCls} />
                    <Input value={s.subtitle || ''} onChange={e => patchSection(s.id, { subtitle: e.target.value })} placeholder="Form subtitle" className={inputCls} />
                    <Input value={s.button_text || ''} onChange={e => patchSection(s.id, { button_text: e.target.value })} placeholder="Button text" className={inputCls} />
                  </div>
                )}
              </Card>
            ))}
            {(editing.sections || []).length === 0 && <EmptyState icon={FileText} text="Add sections to build your page." />}
          </div>
        </div>
      </Panel>
    );
  }

  return (
    <Panel title="Landing Pages" subtitle="Publish conversion pages with lead capture in minutes. Every submission lands in your Audience and campaign attribution." testId="suite-pages"
      action={<Button onClick={() => setEditing({ name: 'New Page', slug: '', sections: [], theme: { background: '#0a0a0a', text: '#ffffff', accent: '#b8ff00' }, form_fields: [{ name: 'name', label: 'Name', type: 'text', required: true }, { name: 'email', label: 'Email', type: 'email', required: true }], published: false })} className="bg-[#b8ff00] text-black font-bold" data-testid="page-new"><Plus size={15} className="mr-1.5" /> New page</Button>}>
      <Card className="p-5 mb-5 space-y-3">
        <Field label="Describe the offer and AI will write the whole page">
          <Textarea value={brief} onChange={e => setBrief(e.target.value)} rows={2} placeholder="Waitlist page for a limited jacket drop — 200 units, early access, free shipping" className={inputCls} data-testid="page-ai-brief" />
        </Field>
        <Button onClick={aiGenerate} disabled={busy === 'ai'} className="bg-violet-500 text-white" data-testid="page-ai-generate">
          {busy === 'ai' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />} Generate landing page
        </Button>
      </Card>

      {loading ? <PanelLoader /> : pages.length === 0 ? <EmptyState icon={FileText} text="No landing pages yet." /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
          {pages.map(p => (
            <Card key={p.id} className="p-4" data-testid={`page-item-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold text-white truncate">{p.name}</p>
                <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${p.published ? 'bg-[#b8ff00]/15 text-[#b8ff00]' : 'bg-zinc-800 text-zinc-400'}`}>{p.published ? 'live' : 'draft'}</span>
              </div>
              <p className="text-[11px] text-zinc-500">/page/{p.slug}</p>
              <p className="text-[11px] text-zinc-400 mt-2 flex items-center gap-3"><span className="flex items-center gap-1"><Eye size={11} /> {p.views} views</span><span>{p.leads} leads</span></p>
              <div className="flex gap-1.5 mt-3">
                <Button size="sm" className="flex-1 bg-zinc-800 text-white text-xs" onClick={() => suiteAPI.getPage(p.id).then(r => setEditing(r.data))} data-testid={`page-edit-${p.id}`}>Edit</Button>
                {p.published && <a href={`${window.location.origin}/page/${p.slug}`} target="_blank" rel="noreferrer"><Button size="sm" className="bg-zinc-800 text-white text-xs"><ExternalLink size={12} /></Button></a>}
                <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => suiteAPI.deletePage(p.id).then(load)}><Trash2 size={13} /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </Panel>
  );
}
